<template>
  <auth-card title="Sign in"
    :error-message="errorMessage">
    <new-password-panel v-if="panel === 'newaccount'"
      id-prefix="signin"
      :username="username"
      :is-submitting="isSubmitting"
      @submit="changePassword" />

    <form v-else
      @submit.prevent="signIn">
      <div class="control mb-4">
        <div class="field">
          <label class="label"
            for="companyid">Company ID</label>
          <input id="companyid"
            class="input"
            type="text"
            placeholder="Company ID"
            maxlength="10"
            autocomplete="organization"
            required
            :disabled="isSubmitting"
            v-model="companyLoginId">
          <span v-if="$v.companyLoginId.$dirty && !$v.companyLoginId.required"
            class="help is-danger ml-1">Required</span>
        </div>

        <div class="field">
          <label class="label"
            for="username">Username</label>
          <input id="username"
            class="input"
            type="text"
            placeholder="Username"
            autocomplete="username"
            required
            :disabled="isSubmitting"
            v-model="username">
          <span v-if="$v.username.$dirty && !$v.username.required"
            class="help is-danger ml-1">Required</span>
        </div>

        <!-- login.css .password-container - a deliberate 40px gap before the Remember me row. -->
        <div class="field password-field">
          <label class="label"
            for="password">Password</label>
          <div class="control has-icons-right">
            <input id="password"
              class="input"
              :type="passwordVisible ? 'text' : 'password'"
              placeholder="Password"
              autocomplete="current-password"
              required
              :disabled="isSubmitting"
              v-model="password">
            <span class="icon is-small is-right is-clickable"
              @click="passwordVisible = !passwordVisible">
              <i class="mdi mdi-18px"
                :class="passwordVisible ? 'mdi-eye-outline' : 'mdi-eye-off-outline'" />
            </span>
          </div>
          <span v-if="$v.password.$dirty && !$v.password.required"
            class="help is-danger ml-1">Required</span>
        </div>

        <div class="field remember-me-container
          is-flex is-justify-content-space-between is-align-items-center">
          <label class="checkbox">
            <input type="checkbox"
              :disabled="isSubmitting"
              v-model="rememberMe">
            Remember me
          </label>
          <a @click.prevent="goToForgotPassword">Forgot Password?</a>
        </div>
      </div>

      <div class="control is-flex is-justify-content-end">
        <button type="submit"
          class="button is-primary"
          :disabled="isSubmitting">
          <span class="icon mr-1">
            <i class="mdi mdi-18px"
              :class="isSubmitting ? 'mdi-loading mdi-spin' : 'mdi-arrow-right'" />
          </span>
          Sign In
        </button>
      </div>
    </form>

    <!-- Over the login page rather than inside the app: no token has been issued at this point,
         so there is no product screen behind it to protect. -->
    <terms-acceptance-modal v-if="pendingTerms"
      :terms="pendingTerms"
      :busy-action="termsAction"
      :submit-error="termsError"
      @accept="acceptTerms"
      @cancel="declineTerms" />
  </auth-card>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import AuthCard from './AuthCard.vue'
import NewPasswordPanel from './NewPasswordPanel.vue'
import TermsAcceptanceModal from './TermsAcceptanceModal.vue'
import AuthRoutes from './route-types.js'
import { getApiErrorMessage } from './ErrorMessage'
import { resolveAppUrl } from './returnUrl'
import { goToLogin } from './loginNavigation'
import { detectPlatform } from '@/services/platform.js'
import { readLegacyRememberedLogin } from './legacyRememberCookies'

const REMEMBER_KEY = 'fq.rememberedLogin'

export default {
  name: 'SignInView',
  components: {
    AuthCard,
    NewPasswordPanel,
    TermsAcceptanceModal
  },
  data() {
    return {
      panel: 'login',
      companyLoginId: '',
      username: '',
      password: '',
      passwordVisible: false,
      rememberMe: false,
      // Held only while the first-login password change is in progress.
      passwordChangeTicket: '',
      // Held here rather than anywhere durable, so closing the tab abandons the ticket - an
      // unaccepted sign-in should leave nothing behind.
      pendingTerms: null,
      termsTicket: '',
      // Which terms button is working: 'accept', 'cancel', or null. Separate from isSubmitting so
      // the spinner lands on the button that was pressed rather than on both.
      termsAction: null,
      // Kept apart from errorMessage, which renders on the login card behind the modal overlay
      // and so would never be seen while the modal is open.
      termsError: '',
      isSubmitting: false,
      errorMessage: ''
    }
  },
  created() {
    // PCK-655: our own remember-me wins; the legacy cookies only fill the gaps.
    const remembered = this.readRememberedLogin()
    const legacy = readLegacyRememberedLogin()

    this.companyLoginId = remembered.companyLoginId || legacy.companyLoginId
    this.username = remembered.username || legacy.username
    this.rememberMe = !!(this.companyLoginId || this.username)
  },
  methods: {
    readRememberedLogin() {
      try {
        const remembered = JSON.parse(localStorage.getItem(REMEMBER_KEY) || 'null')
        if (remembered) {
          return {
            companyLoginId: (remembered.companyLoginId || '').trim(),
            username: (remembered.username || '').trim()
          }
        }
      } catch (e) { /* corrupt entry - fall through to an empty result */ }
      return { companyLoginId: '', username: '' }
    },

    async signIn() {
      this.errorMessage = ''
      this.$v.$touch()
      if (this.$v.$error) return

      this.isSubmitting = true
      try {
        const result = await this.submitCredentials()

        if (result.requiresPasswordChange) {
          this.passwordChangeTicket = result.ticket
          this.panel = 'newaccount'
          return
        }

        if (result.requiresTermsAcceptance) {
          this.termsTicket = result.ticket
          this.pendingTerms = result.terms
          return
        }

        this.rememberCredentials()
        this.enterApp()
      } catch (e) {
        this.errorMessage = getApiErrorMessage(e, 'Sign In failed. Please try again.')
      } finally {
        this.isSubmitting = false
      }
    },

    /** Records the acceptance and finishes the sign-in in one call, with no re-entry of credentials. */
    async acceptTerms() {
      this.termsError = ''
      this.termsAction = 'accept'
      try {
        const { osId, browserId } = detectPlatform()
        await this.$auth.terms.accept({
          ticket: this.termsTicket,
          termsAndConditionsId: this.pendingTerms.id,
          osId,
          browserId,
          location: ''
        })

        this.pendingTerms = null
        this.termsTicket = ''
        this.rememberCredentials()
        this.enterApp()
      } catch (e) {
        // Reported into the modal, not onto the login card: the modal stays open on failure so
        // the user can retry, and the card's alert is behind the overlay.
        this.termsError = getApiErrorMessage(
          e, 'Your acceptance could not be recorded. Please try again.')
      } finally {
        this.termsAction = null
      }
    },

    /**
     * A full navigation rather than a state reset: it takes the entered credentials, the ticket
     * and the modal with the page, so nothing from the abandoned sign-in survives. The returnUrl
     * is carried over so a later successful sign-in still lands where the user was headed.
     */
    async declineTerms() {
      const ticket = this.termsTicket
      this.termsError = ''
      this.termsAction = 'cancel'

      try {
        // Spend the ticket before navigating. A full page load would abort the request in flight
        // and leave the ticket to expire on its own. decline() never rejects.
        if (ticket) await this.$auth.terms.decline(ticket)

        await goToLogin({ returnUrl: this.$route.query.returnUrl })
      } finally {
        // On success the page is already unloading and this never matters. It matters if the
        // navigation is ever blocked - otherwise both buttons stay disabled with no message and
        // the user has no way out of the modal at all.
        this.termsAction = null
      }
    },

    submitCredentials() {
      const { osId, browserId } = detectPlatform()
      return this.$auth.login({
        companyLoginId: this.companyLoginId,
        username: this.username,
        password: this.password,
        machineId: '',
        osId,
        browserId,
        // Geolocation capture is out of scope for FQ-3057. UserSession.Location is write-only in
        // this codebase, so an empty value costs nothing today.
        location: ''
      })
    },

    async changePassword(newPassword) {
      this.errorMessage = ''
      this.isSubmitting = true
      try {
        await this.$auth.changePassword({
          ticket: this.passwordChangeTicket,
          oldPassword: this.password,
          newPassword
        })

        // The ticket is spent, so sign in again with the new password to get a token.
        this.password = newPassword
        this.passwordChangeTicket = ''
        const result = await this.submitCredentials()

        // Changing the password clears ACCOUNT_CREATED, so this second sign-in is the first to
        // reach the terms gate and comes back with a ticket instead of a token. Entering the app
        // on that result would navigate with no cookie and bounce straight back to login.
        if (result && result.requiresTermsAcceptance) {
          this.panel = 'login'
          this.termsTicket = result.ticket
          this.pendingTerms = result.terms
          return
        }

        this.rememberCredentials()
        this.enterApp()
      } catch (e) {
        this.errorMessage = getApiErrorMessage(e, 'Could not change your password. Please try again.')
      } finally {
        this.isSubmitting = false
      }
    },

    rememberCredentials() {
      if (this.rememberMe) {
        localStorage.setItem(REMEMBER_KEY, JSON.stringify({
          companyLoginId: this.companyLoginId,
          username: this.username
        }))
      } else {
        localStorage.removeItem(REMEMBER_KEY)
      }
    },

    enterApp() {
      // Full navigation, not router.push: App.vue runs loadResources once on router.onReady,
      // so an in-app transition would render the shell with no user or permissions loaded.
      window.location.assign(resolveAppUrl(this.$route.query.returnUrl))
    },

    goToForgotPassword() {
      this.$router.push({ name: AuthRoutes.ForgotPasswordView.name })
    }
  },
  validations: {
    companyLoginId: { required },
    username: { required },
    password: { required }
  }
}
</script>

<style lang="scss" scoped>
.label {
  font-weight: 500;
}
.password-field {
  margin-bottom: 40px;
}
.remember-me-container {
  font-size: 0.9rem;
}
</style>
